import { HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    EnvironmentProviders,
    Provider,
    inject,
    makeEnvironmentProviders,
    provideEnvironmentInitializer
} from '@angular/core';
import * as api from '@opentelemetry/api';

import {
    ConfigurationService,
    GuardRegistryService,
    RefreshService,
    RefreshServiceOptions,
    RwcConfig,
    configureRefreshService
} from '@mobi/rwc-utils-common-ng-jslib';
import {
    LOG_RECORD_EXPORTER,
    LoggerConfigOptions,
    SPAN_EXPORTER,
    TracingConfigOptions,
    provideLogging,
    provideTracing
} from '@mobi/rwc-utils-observability-ng-jslib';

import { globalInterceptor } from './internal/http/interceptor/global-interceptor';
import { MtcLogRecordExporter } from './internal/logging/mtc-log-record-exporter';
import { MtcSpanExporter } from './internal/tracing/mtc-span-exporter';

/**
 * @deprecated, it is empty, can be safely simply removed. If it is the only interceptor, you can also remove provideHttpClient() completely.
 */
export const rwcInterceptors: () => HttpInterceptorFn[] = () => [];

export function provideAzureCore(...features: AzureCoreFeature<AzureCoreFeatureKind>[]): EnvironmentProviders {
    // only for backward compatibility: add default features if none are provided
    if (features.length === 0) {
        features.push(withLoggerAppender());
        features.push(withTracing());
    }

    const providers: (Provider | EnvironmentProviders)[] = [
        provideHttpClient(withInterceptors([globalInterceptor()])),
        provideEnvironmentInitializer(() => {
            inject(GuardRegistryService).checkAzureCoreGuard();
            if (!inject(ConfigurationService).getSync('isCloudApp')) {
                throw new Error(
                    'provideAzureCore() can only be used on cloud SPA! Check your providers and use configureDefaultInterceptors() instead.'
                );
            }

            const tkId = inject(ConfigurationService).getTkNameId();

            // Send Span to Azure Application Insights to track which SPA uses the webcomponent
            if (tkId?.endsWith('-webcomp')) {
                const tkIdHostSPA = RwcConfig.getTkId();
                if (tkIdHostSPA) {
                    createWebcompSpan(tkId, tkIdHostSPA);
                }
            }

            // This console message is required by the deployment-check of the rwc stack pipeline
            console.debug('RWC bootstrap finished');
        })
    ];
    features.forEach(feature => providers.push(...feature.ɵproviders));
    return makeEnvironmentProviders(providers);
}

/**
 * To print the logs in the console and send them to the Azure Application Insights.
 */
export function withLoggerAppender(
    options?: LoggerConfigOptions
): AzureCoreFeature<AzureCoreFeatureKind.LoggerAppender> {
    return makeAzureCoreFeature(AzureCoreFeatureKind.LoggerAppender, [
        { provide: LOG_RECORD_EXPORTER, useClass: MtcLogRecordExporter },
        provideLogging(options)
    ]);
}

/**
 * To add tracing headers (mandatory to access the API Gateway) and send spans (http request or custom spans) to the Azure Application Insights.
 */
export function withTracing(options?: TracingConfigOptions): AzureCoreFeature<AzureCoreFeatureKind.Tracing> {
    return makeAzureCoreFeature(AzureCoreFeatureKind.Tracing, [
        { provide: SPAN_EXPORTER, useClass: MtcSpanExporter },
        provideTracing(options)
    ]);
}

/**
 * This will reload the application either every night at 05:00 or on app first navigation after a new version was released.
 * It will poll the version of the app per default every hour but this can be configured.
 */
export function withAutoAppReload(
    options?: RefreshServiceOptions
): AzureCoreFeature<AzureCoreFeatureKind.AutoAppReload> {
    return makeAzureCoreFeature(AzureCoreFeatureKind.AutoAppReload, [
        options ? configureRefreshService(options) : [],
        provideEnvironmentInitializer(() =>
            inject(RefreshService).setupReloadAppEveryNightOrIfStaleAppOnUserNavigation()
        )
    ]);
}

interface AzureCoreFeature<KindT extends AzureCoreFeatureKind> {
    ɵkind: KindT;
    ɵproviders: (Provider | EnvironmentProviders)[];
}

const enum AzureCoreFeatureKind {
    LoggerAppender = 0,
    Tracing = 1,
    AutoAppReload = 2
}

function makeAzureCoreFeature<KindT extends AzureCoreFeatureKind>(
    kind: KindT,
    providers: (Provider | EnvironmentProviders)[]
): AzureCoreFeature<KindT> {
    return {
        ɵkind: kind,
        ɵproviders: providers
    };
}

function createWebcompSpan(tkIdWebcomp: string, tkIdHostSPA: string): void {
    const operationName = `webcomp-loaded: ${tkIdHostSPA} includes ${tkIdWebcomp}`;
    api.trace.getTracer('AzureCore').startSpan(operationName).end();
}
